import React from 'react';
import { Form, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { inspectionTimeUpdate } from '../../modules/analytics';

// Keep styles consistent with financeTable.scss legend/colors
const tierStyles = {
  'super-peak': { border: '#E60F11', background: '#FFD6D6', label: 'Super Peak', labelSuffix: '尖' },
  'peak':       { border: '#FFA53C', background: '#FFE8CD', label: 'Peak',       labelSuffix: '峰' },
  'off-peak':   { border: '#50CD89', background: '#C4EBD9', label: 'Off Peak',   labelSuffix: '平' },
  'low':        { border: '#3CA8FF', background: '#CDE9FF', label: 'Low',        labelSuffix: '谷' },
  'bottom':     { border: '#AB4DCA', background: '#E69ADC', label: 'Bottom',     labelSuffix: '深谷' },
};

const getTierMeta = (key) => tierStyles[key] || { border: '#ccc', background: '#eee', label: key || 'Tier', labelSuffix: '' };
const CODE_TO_KEY = { S: 'super-peak', P: 'peak', O: 'off-peak', L: 'low', B: 'bottom' };
const KEY_TO_CODE = { 'super-peak': 'S', 'peak': 'P', 'off-peak': 'O', 'low': 'L', 'bottom': 'B' };
const KEY_ALIASES = {
  'super-peak': 'super-peak', 'super peak': 'super-peak', 'superpeak': 'super-peak',
  'peak': 'peak',
  'off-peak': 'off-peak', 'off peak': 'off-peak', 'offpeak': 'off-peak',
  'low': 'low',
  'bottom': 'bottom'
};
const normalizeTierKey = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const letter = s.toUpperCase();
  if (CODE_TO_KEY[letter]) return CODE_TO_KEY[letter];
  const key = s.toLowerCase().replace(/\s+/g, '-');
  return KEY_ALIASES[key] || null;
};

/**
 * FinanceBlopsSchedule
 * Props: { form }
 * - Renders a 24-box hour grid with drag-to-select
 * - Clicking a tier swatch immediately applies to the current selection
 * - Persists values in form as `blopsSchedule[0..23]` and `currentBlopsTier`
 */
const FinanceBlopsSchedule = ({ form }) => {
  const dispatch = useDispatch();
  const inspectionTime = useSelector((s) => s.analytics?.inspectionTime || {});

  // Ensure schedule exists in form (24 slots)
  const scheduleRef = React.useRef(false);
  React.useEffect(() => {
    if (scheduleRef.current) return;
    const cur = form.getFieldValue('blopsSchedule');
    let next;
    if (!Array.isArray(cur) || cur.length !== 24) {
      next = new Array(24).fill(null);
    } else {
      next = cur.map((t) => normalizeTierKey(t));
    }
    form.setFieldsValue({ blopsSchedule: next, currentBlopsTier: null });
    scheduleRef.current = true;
  }, [form]);

  const activeTierWatch = Form.useWatch('currentBlopsTier', form);
  const activeTier = (activeTierWatch === undefined) ? null : activeTierWatch;  
  const blopsSchedule = form.getFieldValue('blopsSchedule') || new Array(24).fill(null);

  // Local selection state
  const [selectedHours, setSelectedHours] = React.useState(new Set());
  const isDraggingRef = React.useRef(false);
  React.useEffect(() => {
    const onUp = () => { isDraggingRef.current = false; };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, []);

  // Actions
  const setActiveTier = (tier) => {
    const canon = normalizeTierKey(tier);
    form.setFieldsValue({ currentBlopsTier: canon });
    if (selectedHours.size > 0) {
      const cur = form.getFieldValue('blopsSchedule') || new Array(24).fill(null);
      const next = Array.isArray(cur) ? cur.slice() : new Array(24).fill(null);
      selectedHours.forEach((h) => { next[h] = canon; });
      form.setFieldsValue({ blopsSchedule: next });
      // ensure a repaint in case form batching delays update
      Promise.resolve().then(() => setSelectedHours((prev) => new Set(prev)));
    }
  };

  // Start a NEW selection: clear previous, select just this hour, and unset active tier
  const onHourMouseDown = (h) => {
    isDraggingRef.current = true;
    setSelectedHours(new Set([h]));
    form.setFieldsValue({ currentBlopsTier: null });
  };

  // While dragging, hovering adds hours to the selection only (no painting until tier is chosen)
  const onHourMouseEnter = (h) => {
    if (!isDraggingRef.current) return;
    setSelectedHours((prev) => {
      const next = new Set(prev);
      next.add(h);
      return next;
    });
  };

  // Click no longer toggles (mousedown already handled it)
  const onHourClick = () => {};

  const clearAllHours = () => {
    form.setFieldsValue({ currentBlopsTier: null, blopsSchedule: new Array(24).fill(null) });
    setSelectedHours(new Set());
  };

  const clipboard = inspectionTime?.blopsSchedule;
  const canPaste = Array.isArray(clipboard) && clipboard.length === 24;

  const handleCopy = () => {
    const cur = form.getFieldValue('blopsSchedule') || [];
    if (!Array.isArray(cur) || cur.length !== 24) return;
    const letters = cur.map((t) => {
      const key = normalizeTierKey(t);
      return key ? KEY_TO_CODE[key] : null;
    });
    dispatch(
      inspectionTimeUpdate({
        ...inspectionTime,
        blopsSchedule: letters,
      })
    );
  };

  const handlePaste = () => {
    if (!canPaste) return;
    const existing = form.getFieldValue('blopsSchedule') || [];
    const hasAssigned = Array.isArray(existing) && existing.some((t) => t != null);
    if (hasAssigned && typeof window !== 'undefined') {
      const ok = window.confirm('Replace current schedule with copied hours?');
      if (!ok) return;
    }
    const next = clipboard.map((c) => CODE_TO_KEY[c] || normalizeTierKey(c) || null);
    form.setFieldsValue({ blopsSchedule: next, currentBlopsTier: null });
    setSelectedHours(new Set());
  };

  return (
    <>

      {/* Hidden field to register blopsSchedule with antd form */}
      <Form.Item name="blopsSchedule" hidden>
        <input type="hidden" />
      </Form.Item>

      {/* 24-hour grid (8 columns x 3 rows per SCSS) */}
      <div className="hourGrid">
        {Array.from({ length: 24 }, (_, h) => {
          const tierKey = normalizeTierKey(blopsSchedule[h] || null);
          const meta = tierKey ? getTierMeta(tierKey) : null;
          return (
<button
  key={h}
  type="button"
  className={`hourChip${tierKey ? ' assigned' : ''}${selectedHours.has(h) ? ' selecting' : ''}`}
  onMouseDown={() => onHourMouseDown(h)}
  onMouseEnter={() => onHourMouseEnter(h)}
  onClick={onHourClick}
  style={tierKey ? { background: meta.background, border: `2px solid ${meta.border}` } : undefined}
>
  {String(h).padStart(2, '0')}
</button>
          );
        })}
      </div>

      {/* Tier selector (styled as legend items, accessible radio group) */}
      <Form.Item name="currentBlopsTier" noStyle>
        <div className="blopsTierSelector" role="radiogroup">
          {['super-peak','peak','off-peak','low','bottom'].map((k) => {
            const meta = getTierMeta(k);
            const checked = activeTier === k;
            return (
              <div
                key={k}
                role="radio"
                tabIndex={0}
                className={`legendItem${checked ? ' legendItem-checked' : ''}`}
                aria-checked={checked}
                onClick={() => setActiveTier(k)}
              >
                <span
                  className="tierSwatch"
                  style={{ background: meta.background, border: `2px solid ${meta.border}` }}
                />
                <span className="tierText" style={{ color: meta.border }}>
                  {meta.label}（{meta.labelSuffix}）
                </span>
              </div>
            );
          })}
        </div>
      </Form.Item>


      <div className="hourActions">
        <div className="leftActions">
          <Button type="link" className="actionLink" onClick={handleCopy}>Copy</Button>
          <Button
            type="link"
            className="actionLink"
            disabled={!canPaste}
            onClick={handlePaste}
            title={canPaste ? '' : 'Nothing copied yet'}
          >
            Paste
          </Button>
        </div>
        <Button type="link" className="clearAll actionLink" onClick={clearAllHours}>Clear All</Button>
      </div>
    </>
  );
};

export default FinanceBlopsSchedule;